import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { Markmap } from "markmap-view";
import ReactMarkdown from "react-markdown";
import { Transformer } from "markmap-lib";
import Skeleton from "react-loading-skeleton";
import * as d3 from "d3";
import { revisePyq } from "../services/toolKit";

const transformer = new Transformer();

const convertToMarkdown = (content) => {
  content = content.replace(/```markmap\n([\s\S]*?)```/, "$1");
  if (content.includes("#")) return content;

  const lines = content.split("\n");
  let markdown = "";

  lines.forEach((line) => {
    if (!line.trim()) return;
    const indent = line.match(/^\s*/)[0].length;
    const level = Math.floor(indent / 4);
    let text = line
      .trim()
      .replace(/root\(\((.*?)\)\)/, "$1")
      .replace(/\(\((.*?)\)\)/, "$1")
      .replace(/\((.*?)\)/, "$1");
    const heading = "#".repeat(level + 1);
    markdown += `${heading} ${text}\n`;
  });

  return markdown;
};

const ImageGallery = ({ imageMap, index }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const images = imageMap[index] || [];

  const handleImageClick = (imageData) => {
    setSelectedImage(imageData);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  if (images.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "40px",
          color: "#666",
          fontSize: "16px",
        }}
      >
        No images available for this content.
      </div>
    );
  }

  return (
    <>
      <div
        className="image-gallery"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "15px",
          justifyContent: "center",
          margin: "20px 0",
          padding: "20px",
          width: "100%",
          height: "auto",
        }}
      >
        {images.map((imageData, idx) => (
          <div
            key={idx}
            className="image-container"
            style={{
              cursor: "pointer",
              maxWidth: "30%",
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              transition: "transform 0.2s ease",
            }}
            onClick={() => handleImageClick(imageData)}
            onMouseEnter={(e) => (e.target.style.transform = "scale(1.05)")}
            onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
          >
            <img
              src={imageData.image_link}
              alt={`Gallery image ${idx + 1}`}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "8px",
              }}
            />
          </div>
        ))}
      </div>

      {selectedImage && (
        <div
          className="modal-overlay"
          onClick={closeModal}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              position: "relative",
            }}
          >
            <img
              src={selectedImage.image_link}
              alt="Full size"
              style={{
                maxWidth: "100%",
                maxHeight: "90vh",
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />
            <a
              href={selectedImage.source_link}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                position: "absolute",
                bottom: "-30px",
                left: "0",
                color: "white",
                textDecoration: "underline",
                fontSize: "14px",
              }}
            >
              Source Link
            </a>
            <button
              onClick={closeModal}
              style={{
                position: "absolute",
                top: "-40px",
                right: "0",
                background: "none",
                border: "none",
                color: "white",
                fontSize: "24px",
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const InfographicTab = forwardRef(({ markmapContent }, ref) => {
  const svgRef = useRef();
  const markmapRef = useRef();
  const containerRef = useRef();
  const contentRef = useRef(markmapContent);

  contentRef.current = markmapContent;

  const expandAllNodes = (node) => {
    if (!node) return;
    node.state = 2;
    if (node.children) {
      node.children.forEach(expandAllNodes);
    }
  };

  const createMarkmap = (content, opts = {}) => {
    if (!content || !svgRef.current) return;

    try {
      svgRef.current.innerHTML = "";
      markmapRef.current = null;

      const markdownContent = convertToMarkdown(content);
      const { root } = transformer.transform(markdownContent);

      if (!root || !root.children?.length) {
        throw new Error("No valid data found in markmap content.");
      }

      const containerRect = containerRef.current?.getBoundingClientRect();
      const width = containerRect?.width || 800;
      const height = containerRect?.height || 600;

      svgRef.current.setAttribute("width", width);
      svgRef.current.setAttribute("height", height);

      if (opts.expandAll) {
        root.children.forEach(expandAllNodes);
      }

      const mm = Markmap.create(
        svgRef.current,
        {
          initialExpandLevel: opts.expandAll ? 99 : 0,
          autoFit: true,
        },
        root,
      );

      markmapRef.current = mm;
    } catch (err) {
      console.error("Markmap creation failed:", err);
      if (svgRef.current) {
        svgRef.current.innerHTML = "";
      }
      markmapRef.current = null;
    }
  };

  useImperativeHandle(ref, () => ({
    expandAll: () => {
      if (contentRef.current) {
        createMarkmap(contentRef.current, { expandAll: true });
      }
    },
  }));

  const cleanupMarkmap = () => {
    try {
      if (markmapRef.current) {
        // Properly destroy the markmap instance
        markmapRef.current.destroy?.();
        markmapRef.current = null;
      }
      if (svgRef.current) {
        svgRef.current.innerHTML = "";
        // Remove all event listeners
        svgRef.current.onclick = null;
        svgRef.current.onmousedown = null;
        svgRef.current.onmousemove = null;
        svgRef.current.onmouseup = null;
        svgRef.current.onwheel = null;
      }
    } catch (err) {
      console.error("Markmap cleanup failed:", err);
    }
  };

  useEffect(() => {
    if (markmapContent) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        createMarkmap(markmapContent);
      }, 100);

      return () => {
        clearTimeout(timer);
        cleanupMarkmap();
      };
    }

    return cleanupMarkmap;
  }, [markmapContent]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanupMarkmap;
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        padding: "20px",
        position: "relative",
      }}
    >
      <svg
        ref={svgRef}
        style={{
          width: "100%",
          height: "100%",
          minHeight: "500px",
          display: "block",
        }}
        />
    </div>
  );
});


const TabbedPopup = ({
  markmapContent,
  imageMap,
  index,
  onClose,
  toolkit,
  message,
  setMessages,
  messages,
  loader,
}) => {
  const [activeTab, setActiveTab] = useState("infographic");
  const infographicTabRef = useRef();
  const [pyqData, setPyqData] = useState({
    pyq: "",
    revise: "",
  });
  const hasInfographic = markmapContent && markmapContent.trim().length > 0;
  const hasImages = imageMap && imageMap[index] && imageMap[index].length > 0;
  // const[loader,setLoader]=useState(false);

  // useEffect(() => {
  //   console.log(messages, "testmessgs");

  //   const callRevisePyq = async () => {
  //     try {
  //       setLoader(true);
  //       const response = await revisePyq(message,messages[index].messageId);

  //       setPyqData({
  //         pyq: response.pyq,
  //         revise: response.revise,
  //       });

  //       setMessages((prev) =>
  //         prev.map((msg, i) =>
  //           i === index && (!msg.pyq || !msg.revise)
  //             ? {
  //                 ...msg,
  //                 pyq: response.pyq,
  //                 revise: response.revise,
  //               }
  //             : msg
  //         )
  //       );

  //       console.log("PYQ Revised:", response);
  //     } catch (error) {
  //       console.error("Failed to revise PYQ:", error);
  //     }
  //     finally{
  //       setLoader(false);
  //     }
  //   };

  //   if (
  //     toolkit === true &&
  //     messages?.[index] &&
  //     (messages[index].pyq === ""||messages[index].pyq === null)
  //   ) {
  //     callRevisePyq();
  //   }
  // }, [toolkit]);

  // Always show both tabs, default to infographic if available, otherwise images
  useEffect(() => {
    if (hasInfographic) {
      setActiveTab("infographic");
    } else {
      setActiveTab("images");
    }
  }, [hasInfographic]);

  // Cleanup when switching tabs to prevent SVG errors
  const handleTabSwitch = (tab) => {
    // If switching away from infographic, cleanup the markmap
    if (activeTab === "infographic" && tab !== "infographic") {
      // Force cleanup of any markmap instances
      setTimeout(() => {
        setActiveTab(tab);
      }, 50);
    } else {
      setActiveTab(tab);
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
    >
      <div
        className="modal-content-tabbed"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "90vw",
          height: "80vh",
          backgroundColor: "#fff",
          borderRadius: "12px",
          position: "relative",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 20px 0 20px",
            borderBottom: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", gap: "20px" }}>
            <button
              className={`tab-button ${activeTab === "infographic" ? "active" : ""}`}
              onClick={() => handleTabSwitch("infographic")}
              style={{
                padding: "10px 20px",
                border: "none",
                background:
                  activeTab === "infographic" ? "#007bff" : "transparent",
                color: activeTab === "infographic" ? "white" : "#666",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "16px",
                fontWeight: "500",
                transition: "all 0.2s ease",
              }}
            >
              {/* Infographic */}
              Memorise
            </button>

            {toolkit && (
              <button
                className={`tab-button ${activeTab === "PYQ" ? "active" : ""}`}
                onClick={() => setActiveTab("PYQ")}
                style={{
                  padding: "10px 20px",
                  border: "none",
                  background: activeTab === "PYQ" ? "#007bff" : "transparent",
                  color: activeTab === "PYQ" ? "white" : "#666",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "500",
                  transition: "all 0.2s ease",
                }}
              >
                Analyze PYQs
              </button>
            )}
            {toolkit && (
              <button
                className={`tab-button ${activeTab === "Revise" ? "active" : ""}`}
                onClick={() => setActiveTab("Revise")}
                style={{
                  padding: "10px 20px",
                  border: "none",
                  background:
                    activeTab === "Revise" ? "#007bff" : "transparent",
                  color: activeTab === "Revise" ? "white" : "#666",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "500",
                  transition: "all 0.2s ease",
                }}
              >
                Revise
              </button>
            )}

            {!toolkit && (
              <button
                className={`tab-button ${activeTab === "images" ? "active" : ""}`}
                onClick={() => handleTabSwitch("images")}
                style={{
                  padding: "10px 20px",
                  border: "none",
                  background:
                    activeTab === "images" ? "#007bff" : "transparent",
                  color: activeTab === "images" ? "white" : "#666",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "500",
                  transition: "all 0.2s ease",
                }}
              >
                Images
              </button>
            )}
          </div>

          <button
            className="close-btn"
            onClick={onClose}
            style={{
              fontSize: "24px",
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#666",
              padding: "5px",
              borderRadius: "4px",
              transition: "background-color 0.2s ease",
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = "#f0f0f0")}
            onMouseLeave={(e) =>
              (e.target.style.backgroundColor = "transparent")
            }
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflow: "auto",
            padding: "0",
          }}
        >
          {activeTab === "Revise" && (
            <div>
               <div
                  className="revise-wrapper"
                  dangerouslySetInnerHTML={{
                    __html:
                      messages[index].pyq != ""
                        ? messages[index].revise
                        : pyqData.revise,
                  }}
                />
            </div>
          )}
          {activeTab === "infographic" &&
            (hasInfographic ? (
              <InfographicTab markmapContent={markmapContent} />
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "#666",
                  fontSize: "16px",
                }}
              >
                No infographic available for this content.
              </div>
            ))}
          {activeTab === "images" &&
            (hasImages ? (
              <ImageGallery imageMap={imageMap} index={index} />
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "#666",
                  fontSize: "16px",
                }}
              >
                No images available for this content.
              </div>
            ))}
          {activeTab === "PYQ" && (
            <div>
                   <div
                  className="pyq-wrapper"
                  dangerouslySetInnerHTML={{
                    __html:
                      // by sudz - 260930 - Strip circle emojis from PYQ html
                      (messages[index].pyq != ""
                        ? messages[index].pyq
                        : pyqData.pyq || "").replace(/[🟢🟡🔴]/g, ""),
                  }}
                />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { InfographicTab, ImageGallery };
export default TabbedPopup;
